import { TZDate, tz } from "@date-fns/tz";
import {
  add as _add,
  differenceInMilliseconds as _differenceInMilliseconds,
  endOfDay as _endOfDay,
  endOfHour as _endOfHour,
  endOfMinute as _endOfMinute,
  endOfMonth as _endOfMonth,
  endOfSecond as _endOfSecond,
  format as _format,
  fromUnixTime as _fromUnixTime,
  interval as _interval,
  isAfter as _isAfter,
  isBefore as _isBefore,
  isWithinInterval as _isWithinInterval,
  parseISO as _parseISO,
  startOfDay as _startOfDay,
  startOfHour as _startOfHour,
  startOfMinute as _startOfMinute,
  startOfMonth as _startOfMonth,
  startOfSecond as _startOfSecond,
  sub as _sub,
} from "date-fns";
import type { Duration } from "date-fns";

import { DATETIME_ISO_8601_FORMAT, DATETIME_TIMEZONE } from "../constants";
import { Datetime } from "../types";

export function toTZDate<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? TZDate : TZDate | null {
  if (datetime == null) {
    return null as any;
  }

  return new TZDate(datetime, DATETIME_TIMEZONE);
}

export function toDatetime<Input extends Date | TZDate | null>(date: Input): Input extends Date | TZDate ? Datetime : Datetime | null {
  if (date == null) {
    return null as any;
  }

  return _format(date, DATETIME_ISO_8601_FORMAT, { in: tz(DATETIME_TIMEZONE) }) as Datetime;
}

export function createCurrentDatetime(): Datetime {
  return toDatetime(_startOfSecond(TZDate.tz(DATETIME_TIMEZONE)));
}

export function add<Input extends Datetime | null>(datetime: Input, duration: Duration): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_add(toTZDate(datetime), duration));
}

export function sub<Input extends Datetime | null>(datetime: Input, duration: Duration): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_sub(toTZDate(datetime), duration));
}

export function endOfSecond<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_endOfSecond(toTZDate(datetime)));
}

export function endOfMinute<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_endOfMinute(toTZDate(datetime)));
}

export function endOfHour<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_endOfHour(toTZDate(datetime)));
}

export function endOfDay<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_endOfDay(toTZDate(datetime)));
}

export function endOfMonth<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_endOfMonth(toTZDate(datetime)));
}

export function startOfSecond<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_startOfSecond(toTZDate(datetime)));
}

export function startOfMinute<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_startOfMinute(toTZDate(datetime)));
}

export function startOfHour<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_startOfHour(toTZDate(datetime)));
}

export function startOfDay<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_startOfDay(toTZDate(datetime)));
}

export function startOfMonth<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? Datetime : Datetime | null {
  if (datetime == null) {
    return null as any;
  }

  return toDatetime(_startOfMonth(toTZDate(datetime)));
}

export function isBefore(datetime: Datetime, compare: Datetime): boolean {
  return _isBefore(toTZDate(datetime), toTZDate(compare));
}

export function isAfter(datetime: Datetime, compare: Datetime): boolean {
  return _isAfter(toTZDate(datetime), toTZDate(compare));
}

export function diff(left: Datetime, right: Datetime): number {
  return _differenceInMilliseconds(toTZDate(left), toTZDate(right));
}

export function isInRange(datetime: Datetime, range: [Datetime | null, Datetime | null]): boolean {
  const [dateFrom, dateTo] = range;

  if (dateFrom && dateTo) {
    return _isWithinInterval(toTZDate(datetime), _interval(toTZDate(dateFrom), toTZDate(dateTo)));
  }

  if (dateFrom && !dateTo) {
    return isAfter(datetime, dateFrom);
  }

  if (!dateFrom && dateTo) {
    return isBefore(datetime, dateTo);
  }

  return false;
}

export function fromBackendDate<Input extends string | number | null>(input: Input): Input extends string | number ? Datetime : Datetime | null {
  if (typeof input === "string") {
    return toDatetime(new TZDate(_parseISO(input, { in: tz("UTC") }), DATETIME_TIMEZONE));
  }

  if (typeof input === "number") {
    return toDatetime(new TZDate(_fromUnixTime(input, { in: tz(DATETIME_TIMEZONE) }), DATETIME_TIMEZONE));
  }

  return null as any;
}

export function toBackendDate<Input extends Datetime | null>(datetime: Input): Input extends Datetime ? number : number | null {
  if (datetime == null) {
    return null as any;
  }

  return toTZDate(datetime).getTime() / 1000;
}

export function formatDatetime(datetime: Datetime | null, format = "dd.MM.yyyy", fallback = "-"): string {
  if (datetime == null) {
    return fallback;
  }

  return _format(toTZDate(datetime), format, { in: tz(DATETIME_TIMEZONE) });
}

export function createDefaultSearchDatetimeRange(): [Datetime, Datetime] {
  return [startOfMonth(sub(createCurrentDatetime(), { months: 1 })), endOfDay(createCurrentDatetime())];
}
